/**
 * Token manager for handling token updates and request queuing
 */
import { logger } from "@/lib/logger"

type PendingRequest = {
  id: string
  resolve: (value: any) => void
  reject: (error: any) => void
  url: string
  options: RequestInit
  retryCount: number
}

class TokenManager {
  private pendingRequests: PendingRequest[] = []
  private isWaitingForToken = false
  private tokenUpdateListeners: ((token: string | null) => void)[] = []
  private requestsInProgress = new Set<string>()
  private tokenRequested = false // Flag to prevent multiple token requests
  private lastToken: string | null = null

  // Add a listener for token updates
  addTokenUpdateListener(listener: (token: string | null) => void) {
    this.tokenUpdateListeners.push(listener)
    return () => {
      const index = this.tokenUpdateListeners.indexOf(listener)
      if (index > -1) {
        this.tokenUpdateListeners.splice(index, 1)
      }
    }
  }

  // Get the most recently known token (may be null)
  getLastToken(): string | null {
    return this.lastToken
  }

  // Notify all listeners when token is updated
  notifyTokenUpdate(token: string | null) {
    this.lastToken = token || null
    console.log("TokenManager: Notifying token update to", this.tokenUpdateListeners.length, "listeners")
    this.tokenUpdateListeners.forEach((listener) => listener(token))

    // Reset the token requested flag since we got a new token
    this.tokenRequested = false

    // If we have a new token, retry all pending requests
    if (token && this.pendingRequests.length > 0) {
      console.log("TokenManager: Retrying", this.pendingRequests.length, "pending requests")
      this.retryPendingRequests(token)
    }
  }

  // Generate a unique request ID based on URL and options
  private generateRequestId(url: string, options: RequestInit): string {
    const method = options.method || "GET"
    const body = options.body ? JSON.stringify(options.body) : ""
    return `${method}-${url}-${body}`
  }

  // Check if we should request a new token
  shouldRequestToken(): boolean {
    if (this.tokenRequested) {
      console.log("TokenManager: Token already requested, not requesting again")
      return false
    }
    this.tokenRequested = true
    return true
  }

  // Queue a request to be retried when a new token is available
  queueRequest(url: string, options: RequestInit): Promise<Response> {
    return new Promise((resolve, reject) => {
      const requestId = this.generateRequestId(url, options)

      // Check if this exact request is already queued
      const existingRequest = this.pendingRequests.find((req) => req.id === requestId)
      if (existingRequest) {
        console.log("TokenManager: Request already queued, not adding duplicate", url)
        // Return the same promise as the existing request
        existingRequest.resolve = resolve
        existingRequest.reject = reject
        return
      }

      console.log("TokenManager: Queueing request for", url)
      this.pendingRequests.push({
        id: requestId,
        resolve,
        reject,
        url,
        options,
        retryCount: 0,
      })

      // Set flag that we're waiting for a token
      this.isWaitingForToken = true
    })
  }

  // Retry all pending requests with the new token
  private async retryPendingRequests(token: string) {
    if (this.pendingRequests.length === 0) {
      return
    }

    const requests = [...this.pendingRequests]
    this.pendingRequests = []
    this.isWaitingForToken = false

    console.log("TokenManager: Retrying", requests.length, "requests with new token")

    // Group requests by ID to handle duplicates
    const uniqueRequests = new Map<string, PendingRequest>()
    requests.forEach((request) => {
      if (!uniqueRequests.has(request.id)) {
        uniqueRequests.set(request.id, request)
      }
    })

    for (const request of uniqueRequests.values()) {
      try {
        // Update the authorization header with the new token
        const headers = new Headers(request.options.headers)
        headers.set("Authorization", `Bearer ${token}`)

        console.log(`TokenManager: Retrying request for ${request.url}`)

        const response = await fetch(request.url, {
          ...request.options,
          headers,
        })

        // Resolve the promise with the response
        request.resolve(response)
      } catch (error) {
        logger.error(error)
        request.reject(error)
      }
    }
  }

  // Check if we're currently waiting for a token
  isWaitingForNewToken(): boolean {
    return this.isWaitingForToken
  }

  // Check if a specific request is already queued
  isRequestQueued(url: string, options: RequestInit): boolean {
    const requestId = this.generateRequestId(url, options)
    return this.pendingRequests.some((req) => req.id === requestId)
  }

  // Clear all pending requests (e.g., on component unmount)
  clearPendingRequests() {
    console.log("TokenManager: Clearing", this.pendingRequests.length, "pending requests")
    this.pendingRequests.forEach((request) => {
      request.reject(new Error("Request cancelled"))
    })
    this.pendingRequests = []
    this.isWaitingForToken = false
    this.requestsInProgress.clear()
    this.tokenRequested = false
  }
}

// Export a singleton instance
export const tokenManager = new TokenManager()
